import { useEffect, useRef, useState } from "react";

/**
 * useInView — déclenche `inView=true` quand l'élément entre dans la fenêtre.
 * `once: true` (par défaut) garde l'état actif après le 1er déclenchement.
 */
export function useInView({ threshold = 0.2, rootMargin = "0px 0px -80px 0px", once = true } = {}) {
  const ref = useRef(null);
  // Default to true so above-the-fold elements show immediately on first paint;
  // IntersectionObserver below will keep below-fold elements hidden until they
  // scroll into view.
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Reveal above-the-fold elements immediately to avoid stuck-at-opacity:0
    // when IntersectionObserver does not fire for already-visible nodes.
    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) {
      setInView(true);
      if (once) return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, inView };
}
